import { useEffect, useRef } from "react";
import { ATTACHMENT_ACCEPT } from "@touchworks/shared/support/attachmentTypes.js";
import AttachmentTray from "./AttachmentTray";
import { spotlightTargetProps } from "@/components/featureSpotlight/spotlightTargetProps";

/**
 * Teto de altura do campo no painel flutuante, em px (cerca de cinco linhas).
 *
 * Mais que isto e a conversa acima vira uma tira: o painel tem 560px de altura
 * total, e o campo comeria metade dela.
 */
const COMPACT_MAX_HEIGHT = 112;

/**
 * The one place a question to Lucas gets written.
 *
 * Shared by the hero and by the open conversation instead of each rendering its
 * own input: attachments, paste handling and the send rules would otherwise
 * exist twice and drift, and the two copies would disagree about what can be
 * attached.
 *
 * Shift+Enter inserts a newline; plain Enter submits.
 */

function PaperclipGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-[17px] w-[17px]">
      <path
        d="M14.5 8.5l-5.1 5.1a2.7 2.7 0 01-3.8-3.8l6-6a1.8 1.8 0 012.6 2.6l-6 6a.9.9 0 01-1.3-1.3l5.1-5.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** O quadrado de "parar" — o mesmo lugar do botão de enviar. */
function StopGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-[12px] w-[12px]">
      <rect x="5" y="5" width="10" height="10" rx="2" fill="currentColor" />
    </svg>
  );
}

/**
 * Fica fora da caixa, sob ela, e aparece nos dois estados — inclusive com a
 * conversa aberta, que é justamente quando a pessoa está lendo a resposta e
 * decidindo se confia nela.
 */
export const LUCAS_DISCLAIMER =
  "O Lucas é uma inteligência artificial e pode cometer erros.";

export default function LucasComposer({
  value,
  onChange,
  onSubmit,
  attachments = [],
  onAttach,
  onPaste,
  onRemoveAttachment,
  placeholder = "Descreva o que aconteceu ou faça uma pergunta",
  hint,
  autoFocus = false,
  // Primeiro gesto de interesse: e ele que acorda a conversa no provider,
  // segundos antes de qualquer envio, para que a assinatura do socket ja
  // exista quando a resposta voltar.
  onFocus,
  disabled = false,
  replyTo = null,
  onCancelReply,
  // Um turno está em curso: o botão de enviar vira o de parar.
  busy = false,
  onStop,
  /**
   * Versão estreita, para o painel flutuante.
   *
   * A caixa original foi desenhada para a coluna de 760px da home: com 380px
   * ela vira um retângulo alto e vazio, com os botões perdidos lá embaixo.
   * Compacto encolhe o respiro e junta os controles ao texto.
   */
  compact = false,
}) {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  /**
   * O campo cresce com o texto, no painel estreito.
   *
   * Na home o campo é alto e flexível: cabem duas linhas folgadas e a moldura
   * reserva 118px fixos, número de que o cálculo de altura da conversa depende
   * (`h-[calc(100dvh-262px)]`). Por isso o crescimento vale SÓ no compacto:
   * mexer na altura lá estouraria a viewport.
   *
   * No painel, sem isto, escrever a segunda linha empurrava a primeira para
   * fora e aparecia uma barra de rolagem dentro de um campo de uma linha. A
   * pessoa via o que acabou de digitar sumir.
   *
   * O teto existe para a conversa não virar uma tira: passando dele, aí sim
   * rola por dentro.
   */
  useEffect(() => {
    const campo = textareaRef.current;
    if (!campo || !compact) return;

    campo.style.height = "auto";
    campo.style.height = `${Math.min(campo.scrollHeight, COMPACT_MAX_HEIGHT)}px`;
  }, [value, compact, replyTo, attachments.length]);

  // Enquanto o Lucas responde não se envia outra — o mesmo botão vira parar.
  const canSubmit = !disabled && !busy && (value.trim() || attachments.length > 0);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSubmit) onSubmit();
    }
  };

  const handlePaste = (event) => {
    // Only swallow the paste when it actually carried a file — a plain text
    // paste has to stay a text paste.
    if (onPaste?.(event.clipboardData)) event.preventDefault();
  };

  const handleFiles = (event) => {
    onAttach?.(event.target.files);
    // Reset so picking the same file twice in a row still fires onChange.
    event.target.value = "";
  };

  return (
    <>
    <div
      onClick={() => textareaRef.current?.focus()}
      {...(compact ? {} : spotlightTargetProps("dashboard.lucas-composer"))}
      className={`flex cursor-text flex-col rounded-[20px] border border-black/10 bg-white/80 text-left shadow-[0_18px_44px_rgba(20,22,45,.12)] backdrop-blur-[22px] transition-colors focus-within:border-black/25 hover:border-black/20 dark:border-white/[.13] dark:bg-[#0e0e14]/[.66] dark:shadow-[0_24px_64px_rgba(0,0,0,.5)] dark:focus-within:border-white/30 dark:hover:border-white/30 ${
        compact ? "min-h-[72px] gap-1.5 p-3 pb-2" : "min-h-[118px] gap-3 p-5 pb-3.5"
      }`}
    >
      {/* O trecho que está sendo respondido fica à vista enquanto se escreve —
          sem isso a pessoa perde de vista a que estava respondendo, e o "X" é
          a única forma de desistir da citação. */}
      {replyTo && (
        <div className="flex items-start gap-2 rounded-[10px] border-l-[3px] border-[#1d84d6] bg-black/[.06] px-2.5 py-1.5 dark:border-[#84caff] dark:bg-white/[.08]">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[11px] font-semibold text-[#1d6fb8] dark:text-[#9fd4ff]">
              Respondendo
            </span>
            <span className="line-clamp-2 text-[12.5px] leading-[1.4] text-[#3d3f4d] dark:text-white/70">
              {replyTo.content}
            </span>
          </div>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onCancelReply?.();
            }}
            aria-label="Cancelar resposta"
            className="ml-auto grid h-6 w-6 flex-none place-items-center rounded-md text-[#6c6f80] transition-colors hover:bg-black/[.06] dark:text-white/50 dark:hover:bg-white/[.10]"
          >
            ✕
          </button>
        </div>
      )}

      <AttachmentTray attachments={attachments} onRemove={onRemoveAttachment} />

      <label htmlFor="lucas-ask" className="sr-only">
        Descreva o que aconteceu ou faça uma pergunta ao Lucas
      </label>
      <textarea
        id="lucas-ask"
        ref={textareaRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={onFocus}
        rows={compact ? 1 : 2}
        autoFocus={autoFocus}
        placeholder={placeholder}
        /**
         * O tamanho do texto é o MESMO nos dois lugares.
         *
         * O compacto encolhe respiro (padding, gaps, altura), nunca a letra: a
         * conversa do painel flutuante é a mesma do dashboard, e letra menor
         * num lugar faria a pessoa sentir que mudou de produto ao trocar de
         * tela. O que cabe menos no painel é espaço vazio, não conteúdo.
         */
        className={`w-full resize-none border-0 bg-transparent p-0 text-[14.5px] leading-[1.5] text-[#15161f] outline-none placeholder:text-[#8a8d9e] dark:text-white dark:placeholder:text-white/40 ${
          compact
            ? // `flex-none`: a altura é escrita pelo efeito acima. Com `flex-1`
              // o flex esticaria o campo e o crescimento não apareceria.
              "flex-none overflow-y-auto"
            : "flex-1"
        }`}
      />

      <div className={`mt-auto flex items-center ${compact ? "gap-1" : "gap-2"}`}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          onChange={handleFiles}
          className="hidden"
        />
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            fileInputRef.current?.click();
          }}
          aria-label="Anexar arquivo"
          className="grid h-[30px] w-[30px] flex-none place-items-center rounded-lg text-[#6c6f80] transition-colors hover:bg-black/[.05] hover:text-[#15161f] dark:text-white/50 dark:hover:bg-white/[.08] dark:hover:text-white"
        >
          <PaperclipGlyph />
        </button>

        {hint && (
          <span className="truncate font-mono text-[11px] leading-none text-[#9a9dad] dark:text-white/[.28]">
            {hint}
          </span>
        )}

        {busy ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onStop?.();
            }}
            aria-label="Parar resposta"
            className="ml-auto grid h-[34px] w-[34px] flex-none place-items-center rounded-[11px] bg-[#15161f] text-white transition-opacity hover:opacity-80 dark:bg-white dark:text-[#0a0a0f]"
          >
            <StopGlyph />
          </button>
        ) : (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSubmit();
            }}
            disabled={!canSubmit}
            aria-label="Enviar pergunta"
            className="ml-auto grid h-[34px] w-[34px] flex-none place-items-center rounded-[11px] bg-[#15161f] text-[15px] font-bold text-white transition-opacity disabled:opacity-30 dark:bg-white dark:text-[#0a0a0f] dark:disabled:opacity-40"
          >
            ↑
          </button>
        )}
      </div>
    </div>

    <p
      className={`m-0 text-center text-[11.5px] leading-none text-[#8a8d9e] dark:text-white/40 ${
        compact ? "mt-1.5" : "mt-2"
      }`}
    >
      {LUCAS_DISCLAIMER}
    </p>
    </>
  );
}
